import React, { useContext, useEffect, useState, useMemo } from 'react';

import PropTypes from 'prop-types';

import Button from '@mui/material/Button';

import ModalTemplate from './_ModalTemplate';
import SimpleSelect from '../Components/SimpleSelect';
import SimpleTextInput from '../Components/SimpleTextInput';
import ReadOnlyInput from '../Components/ReadOnlyInput';
import { GBENSContext } from '../GBENSContainer';

export const EnsRequestorModal = ({ open, setOpen, screen }) => {
  const modal = 'ENSRequestor';
  const close = () => {
    setOpen(false);
  };

  return (
    <ModalTemplate
      open={open}
      onClose={close}
      title="ENS Requestor"
      text="Persons lodging the declaration"
      body={
        <div>
          <ReadOnlyInput name="Country" screen={screen} modal={modal} id="country" />
          <ReadOnlyInput name="EORI:" screen={screen} modal={modal} id="EORI" />
          <ReadOnlyInput
            name="Company Name:"
            screen={screen}
            modal={modal}
            placeholder="Please input EORI number"
            id="companyName"
          />
          <ReadOnlyInput
            name="Street Address:"
            screen={screen}
            modal={modal}
            placeholder="Address Line 1..."
            id="streetAddress"
          />
          <ReadOnlyInput
            name="Town / City:"
            screen={screen}
            modal={modal}
            id="townCity"
            placeholder="Address Line 2..."
          />
          <ReadOnlyInput name="Postcode:" screen={screen} modal={modal} id="postcode" placeholder="Address Line 3..." />
        </div>
      }
      buttons={[
        <div key={0} />,
        <Button key={1} onClick={close}>
          Close
        </Button>,
      ]}
    />
  );
};

EnsRequestorModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
};

export const VehicleModal = ({ open, setOpen, screen, onTransportChanged }) => {
  const modal = 'Vehicle';
  const modalLocal = '_' + modal;
  const { data, moveData, deleteData, referenceData, isReadOnly } = useContext(GBENSContext);

  const vehicleIDTooltip = useMemo(() => {
    // if mode of transport is RoRo Unaccompanied
    if (parseInt(data?.TransportAndJourney?.[modalLocal]?.ModeOfTransport) === 11) {
      return 'Please enter the Ship IMO number. These should be entered without spaces or special characters.';
    }
    return 'Please enter the Vehicle ID. These should be entered without spaces or special characters.';
  }, [data]);

  useEffect(() => {
    if (open) {
      moveData(screen, modal, modalLocal, false);
    }
  }, [open]);

  const dontSave = () => {
    deleteData(modalLocal);
    setOpen(false);
  };

  const save = () => {
    moveData(screen, modalLocal, modal, true);
    setOpen(false);
  };

  const rowProps = {
    leftColumnWidth: '35%',
    rightColumnWidth: '65%',
  };

  return (
    <ModalTemplate
      open={open}
      onClose={dontSave}
      title="Vehicle Details"
      body={
        <div>
          <SimpleSelect
            name="Mode of Transport"
            screen={screen}
            modal={modalLocal}
            id="ModeOfTransport"
            options={referenceData.modeOfTransport}
            rowProps={rowProps}
            onBeforeChange={onTransportChanged}
            disabled={isReadOnly}
          />
          <SimpleTextInput
            name="Vehicle ID:"
            screen={screen}
            modal={modalLocal}
            maxLength={27}
            placeholder="Please input vehicle ID"
            id="vehicleID"
            rowProps={rowProps}
            toolTip={vehicleIDTooltip}
            disabled={isReadOnly}
          />
          <SimpleSelect
            name="Nationality"
            screen={screen}
            modal={modalLocal}
            id="Nationality"
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            disabled={isReadOnly}
          />
          <SimpleTextInput
            name="Trailer / Container Number:"
            screen={screen}
            modal={modalLocal}
            placeholder="The Trailer or Container number must be entered"
            id="trailerNo"
            rowProps={rowProps}
            disabled={isReadOnly}
          />
        </div>
      }
      buttons={[
        <Button key={0} onClick={dontSave}>
          Close
        </Button>,
        <Button key={1} onClick={save} disabled={isReadOnly}>
          Add
        </Button>,
      ]}
    />
  );
};

VehicleModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
};

export const PlaceOfLoadingModal = ({ open, setOpen, screen, isConsignment }) => {
  const modal = 'PlaceOfLoading';
  const modalLocal = '_' + modal;
  const { moveData, deleteData, referenceData, moveConsignmentData, deleteConsignmentData, isReadOnly } =
    useContext(GBENSContext);
  useEffect(() => {
    if (open) {
      isConsignment ? moveConsignmentData(modal, modalLocal, false) : moveData(screen, modal, modalLocal, false);
    }
  }, [open]);

  const dontSave = () => {
    isConsignment ? deleteConsignmentData(modalLocal) : deleteData(modalLocal);
    setOpen(false);
  };

  const save = () => {
    isConsignment ? moveConsignmentData(modalLocal, modal, true) : moveData(screen, modalLocal, modal, true);
    setOpen(false);
  };

  return (
    <ModalTemplate
      open={open}
      onClose={dontSave}
      title="Place of Loading"
      text="When all the goods are loaded at one location, these details should be added. If the goods are loaded at the exporters site(s), add the location on the Consignment(s)."
      body={
        <div>
          <SimpleSelect
            name="Country"
            screen={screen}
            consignment={isConsignment}
            modal={modalLocal}
            id="country"
            options={referenceData.countryOfOriginCC}
            disabled={isReadOnly}
          />
          <SimpleTextInput
            name="Place Name"
            screen={screen}
            consignment={isConsignment}
            modal={modalLocal}
            id="placeName"
            toolTip="Enter the name of the warehouse or place of loading."
            disabled={isReadOnly}
          />
          <SimpleTextInput
            name="Post code"
            screen={screen}
            consignment={isConsignment}
            modal={modalLocal}
            id="postcode"
            toolTip="Enter the postcode of the warehouse or place of loading."
            disabled={isReadOnly}
          />
        </div>
      }
      buttons={[
        <Button key={0} onClick={dontSave}>
          Close
        </Button>,
        <Button key={1} onClick={save} disabled={isReadOnly}>
          Add
        </Button>,
      ]}
    />
  );
};

PlaceOfLoadingModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
  isConsignment: PropTypes.bool,
};

export const PlaceOfUnloadingModal = ({ open, setOpen, screen, isConsignment }) => {
  const modal = 'PlaceOfUnloading';
  const modalLocal = '_' + modal;
  const { moveData, deleteData, referenceData, moveConsignmentData, deleteConsignmentData, isReadOnly } =
    useContext(GBENSContext);
  useEffect(() => {
    if (open) {
      isConsignment ? moveConsignmentData(modal, modalLocal, false) : moveData(screen, modal, modalLocal, false);
    }
  }, [open]);

  const dontSave = () => {
    isConsignment ? deleteConsignmentData(modalLocal) : deleteData(modalLocal);
    setOpen(false);
  };

  const save = () => {
    isConsignment ? moveConsignmentData(modalLocal, modal, true) : moveData(screen, modalLocal, modal, true);
    setOpen(false);
  };

  return (
    <ModalTemplate
      open={open}
      onClose={dontSave}
      title="Place of Unloading"
      text="When all the goods are unloaded at one location, these details should be added. If the goods are located at the exporters site, update the Consignment details."
      body={
        <div>
          <SimpleSelect
            name="Country"
            screen={screen}
            consignment={isConsignment}
            modal={modalLocal}
            id="country"
            options={referenceData.countryOfOriginCC}
            disabled={isReadOnly}
          />
          <SimpleTextInput
            name="Place Name"
            screen={screen}
            consignment={isConsignment}
            modal={modalLocal}
            id="placeName"
            toolTip="Enter the name of the warehouse or place of unloading."
            disabled={isReadOnly}
          />
          <SimpleTextInput
            name="Post code"
            screen={screen}
            consignment={isConsignment}
            modal={modalLocal}
            id="postcode"
            toolTip="Enter the postcode of the warehouse or place of unloading."
            disabled={isReadOnly}
          />
        </div>
      }
      buttons={[
        <Button key={0} onClick={dontSave}>
          Close
        </Button>,
        <Button key={1} onClick={save} disabled={isReadOnly}>
          Add
        </Button>,
      ]}
    />
  );
};

PlaceOfUnloadingModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
  isConsignment: PropTypes.bool,
};

const RenderTransitingCountry = ({ number, screen, modal, options, rowProps, hidden, disabled }) => {
  return (
    <div style={{ display: hidden ? 'none' : 'block' }}>
      <SimpleSelect
        name={`Transiting Country ${number}`}
        screen={screen}
        modal={modal}
        id={`transCountry${number}`}
        options={options}
        rowProps={rowProps}
        disabled={disabled}
      />
    </div>
  );
};

RenderTransitingCountry.propTypes = {
  number: PropTypes.number,
  screen: PropTypes.string,
  modal: PropTypes.string,
  options: PropTypes.array,
  rowProps: PropTypes.object,
  hidden: PropTypes.bool,
};

export const RoutingModal = ({ open, setOpen, screen }) => {
  const modal = 'Route';
  const modalLocal = '_' + modal;
  const { data, moveData, deleteData, referenceData, isReadOnly } = useContext(GBENSContext);

  const [totalTransitingCountries, setTotalTransitingCountries] = useState(1);

  useEffect(() => {
    if (open) {
      const routes = data[screen][modal];
      Object.keys(routes).forEach((key) => {
        if (key.startsWith('transCountry')) {
          const transCountry = key.replace('transCountry', '');
          if (parseInt(transCountry) > totalTransitingCountries) {
            setTotalTransitingCountries(parseInt(transCountry));
          }
        }
      });

      moveData(screen, modal, modalLocal, false);
    }
  }, [open]);

  const dontSave = () => {
    deleteData(modalLocal);
    setOpen(false);
  };

  const save = () => {
    moveData(screen, modalLocal, modal, true);
    setOpen(false);
  };

  const rowProps = {
    leftColumnWidth: '40%',
    rightColumnWidth: '60%',
  };

  const MAX_TRANSITING_COUNTRIES = 7;

  const addTransitingCountry = () => {
    let newTotal = totalTransitingCountries + 1;
    if (newTotal > MAX_TRANSITING_COUNTRIES) newTotal = MAX_TRANSITING_COUNTRIES;
    setTotalTransitingCountries(newTotal);
  };

  return (
    <ModalTemplate
      open={open}
      onClose={() => setOpen(false)}
      title="Routing Countries"
      sx={{
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      body={
        <div
          style={{
            maxHeight: '75vh',
            overflowY: 'auto',
            padding: '10px',
          }}
        >
          <SimpleSelect
            name="Departure Country*"
            screen={screen}
            modal={modalLocal}
            id="departureCountry"
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            disabled={isReadOnly}
          />
          <RenderTransitingCountry
            number={1}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 1}
            disabled={isReadOnly}
          />
          <RenderTransitingCountry
            number={2}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 2}
            disabled={isReadOnly}
          />
          <RenderTransitingCountry
            number={3}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 3}
            disabled={isReadOnly}
          />
          <RenderTransitingCountry
            number={4}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 4}
            disabled={isReadOnly}
          />
          <RenderTransitingCountry
            number={5}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 5}
            disabled={isReadOnly}
          />
          <RenderTransitingCountry
            number={6}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 6}
            disabled={isReadOnly}
          />
          <RenderTransitingCountry
            number={7}
            screen={screen}
            modal={modalLocal}
            options={referenceData.countryOfOriginId}
            rowProps={rowProps}
            hidden={totalTransitingCountries < 7}
            disabled={isReadOnly}
          />

          {totalTransitingCountries < MAX_TRANSITING_COUNTRIES && (
            <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0 20px 0' }}>
              <Button variant="outlined" onClick={addTransitingCountry} disabled={isReadOnly}>
                Add Another Transiting Country
              </Button>
            </div>
          )}

          <ReadOnlyInput
            name="Destination Country"
            screen={screen}
            modal={modal}
            id="destinationCountry"
            variant="standard"
            fullWidth
            lookup={referenceData.countryOfOriginId}
            rowProps={rowProps}
          />
        </div>
      }
      buttons={[
        <Button key={0} onClick={dontSave}>
          Close
        </Button>,
        <Button key={1} onClick={save} disabled={isReadOnly}>
          Add
        </Button>,
      ]}
    />
  );
};

RoutingModal.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  screen: PropTypes.string,
};
